/* The guide layer: a right-side panel that explains every surface of the app.

   - GuidePanel: fixed right panel, context-aware (topics for the current
     route float to the top), searchable, accordion topics.
   - HelpDot: a small "?" you can drop on any card header — opens the panel
     scrolled to that topic.
   - Term: dotted-underline glossary term with a hover definition; click
     opens the full topic in the panel.

   Cross-component wiring is a window CustomEvent ('fl:guide') so any page
   can open a topic without prop plumbing. Panel open state + per-topic
   expansion are session-local (not persisted): the panel should never be
   in the way on next visit. */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { GUIDE_TOPICS, GLOSSARY, topicsForPath } from '../data/guideContent';
import './GuidePanel.css';

export function openGuide(topicId) {
  window.dispatchEvent(new CustomEvent('fl:guide', { detail: { topic: topicId || null } }));
}

export function HelpDot({ topic, label }) {
  return (
    <button
      type="button"
      className="gd-dot"
      title={label || 'What is this? Open the guide'}
      aria-label={label || 'Open the guide for this section'}
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); openGuide(topic); }}
    >
      ?
    </button>
  );
}

export function Term({ children, term }) {
  const key = (term || String(children)).toLowerCase();
  const entry = GLOSSARY[key];
  if (!entry) return children;
  return (
    <button
      type="button"
      className="gd-term"
      title={`${entry.def}  —  click to learn more`}
      onClick={(e) => { e.stopPropagation(); openGuide(entry.topic); }}
    >
      {children}
    </button>
  );
}

/* Markdown-lite renderer: paragraphs on blank lines, "- " bullets,
   **bold**, `code`. Enough for guide bodies without a dependency. */
function Inline({ text }) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (p.startsWith('`') && p.endsWith('`')) return <code key={i}>{p.slice(1, -1)}</code>;
    return p;
  });
}

function Body({ md }) {
  const blocks = md.trim().split(/\n\s*\n/);
  return blocks.map((block, i) => {
    const lines = block.split('\n');
    if (lines.every((l) => l.trim().startsWith('- '))) {
      return (
        <ul key={i} className="gd-list">
          {lines.map((l, j) => <li key={j}><Inline text={l.trim().slice(2)} /></li>)}
        </ul>
      );
    }
    return <p key={i}><Inline text={lines.join(' ')} /></p>;
  });
}

/* Prefill the command bar with an example request. Mirrors CommandBar's
   openCommandBar() (not imported — that would be a circular module ref):
   in-project pages carry #project-command; elsewhere the overlay palette. */
function tryExample(ex, close) {
  const detail = { verb: ex.verb === 'do' ? 'do' : 'ask', instruction: ex.text };
  const event = document.getElementById('project-command') ? 'fl:project-cmdk' : 'fl:cmdk';
  window.dispatchEvent(new CustomEvent(event, { detail }));
  close();
}

function Topic({ topic, expanded, onToggle, innerRef, onClose }) {
  return (
    <div className={`gd-topic ${expanded ? 'gd-topic-open' : ''}`} ref={innerRef}>
      <button type="button" className="gd-topic-head" onClick={onToggle} aria-expanded={expanded}>
        <span className="gd-topic-emoji" aria-hidden>{topic.emoji}</span>
        <span className="gd-topic-title">{topic.title}</span>
        <span className="gd-topic-chev" aria-hidden>{expanded ? '−' : '+'}</span>
      </button>
      {expanded && (
        <div className="gd-topic-body">
          <Body md={topic.body} />
          {(topic.examples || []).length > 0 && (
            <div className="gd-try">
              <div className="gd-try-label">Try it — sends the request to the command bar</div>
              {topic.examples.map((ex) => (
                <button
                  key={ex.text}
                  type="button"
                  className="gd-try-chip"
                  onClick={() => tryExample(ex, onClose)}
                  title={ex.verb === 'do' ? 'Prefills Instruct — the agent files a reviewable proposal' : 'Prefills Ask — answered from the project brain'}
                >
                  <span className={`gd-try-verb ${ex.verb === 'do' ? 'gd-try-do' : ''}`}>{ex.verb === 'do' ? 'Instruct' : 'Ask'}</span>
                  {ex.text}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function GuidePanel() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [query, setQuery] = useState('');
  const location = useLocation();
  const topicRefs = useRef({});

  useEffect(() => {
    const onGuide = (e) => {
      const id = e.detail?.topic;
      setOpen(true);
      if (id) {
        setExpanded((x) => ({ ...x, [id]: true }));
        // Scroll after the panel has rendered.
        setTimeout(() => {
          topicRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 60);
      }
    };
    window.addEventListener('fl:guide', onGuide);
    return () => window.removeEventListener('fl:guide', onGuide);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const { here, rest } = useMemo(() => topicsForPath(location.pathname), [location.pathname]);

  const q = query.trim().toLowerCase();
  const filter = (list) =>
    !q
      ? list
      : list.filter((t) =>
          t.title.toLowerCase().includes(q)
          || t.body.toLowerCase().includes(q)
          || (t.keywords || []).some((k) => k.includes(q))
          || (t.examples || []).some((e) => e.text.toLowerCase().includes(q)));
  const hereShown = filter(here);
  const restShown = filter(rest);

  if (!open) return null;

  return (
    <aside className="gd-panel" role="complementary" aria-label="Platform guide">
      <div className="gd-head">
        <span className="gd-head-title">📖 Guide</span>
        <span className="gd-head-hint">esc to close</span>
        <button type="button" className="gd-close" onClick={() => setOpen(false)} aria-label="Close guide">✕</button>
      </div>
      <input
        className="gd-search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search the guide…"
        aria-label="Search guide topics"
      />
      <div className="gd-body">
        {hereShown.length > 0 && (
          <>
            <div className="gd-group-label">On this page</div>
            {hereShown.map((t) => (
              <Topic
                key={t.id}
                topic={t}
                expanded={!!expanded[t.id]}
                onToggle={() => setExpanded((x) => ({ ...x, [t.id]: !x[t.id] }))}
                innerRef={(el) => { topicRefs.current[t.id] = el; }}
                onClose={() => setOpen(false)}
              />
            ))}
          </>
        )}
        {restShown.length > 0 && (
          <>
            <div className="gd-group-label">{hereShown.length > 0 ? 'Everything else' : 'All topics'}</div>
            {restShown.map((t) => (
              <Topic
                key={t.id}
                topic={t}
                expanded={!!expanded[t.id]}
                onToggle={() => setExpanded((x) => ({ ...x, [t.id]: !x[t.id] }))}
                innerRef={(el) => { topicRefs.current[t.id] = el; }}
                onClose={() => setOpen(false)}
              />
            ))}
          </>
        )}
        {hereShown.length === 0 && restShown.length === 0 && (
          <div className="gd-empty">No topics match “{query}” — try the chat assistant: it answers free-form questions about the platform.</div>
        )}
      </div>
      <div className="gd-foot">
        💡 Prefer asking? Press <kbd>⌘K</kbd> and ask in your own words — the assistant knows this whole guide.
      </div>
    </aside>
  );
}

export const GUIDE_TOPIC_COUNT = GUIDE_TOPICS.length;
